import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Link
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpIcon from '@mui/icons-material/Help';
import PauseCircleIcon from '@mui/icons-material/PauseCircle'; // For trialing

// AppUser type will be passed from App.tsx (or a shared types file)
// No local AppUser interface needed here anymore
// interface AppUser {
//   subscriptionStatus?: string;
//   // other user fields...
// }

// Assuming AppUser type is imported or defined in a way that includes subscriptionStatus
// For this component, we only care about a user object that *might* have a subscriptionStatus.
interface SubscriptionStatusDisplayProps {
  currentUser: { subscriptionStatus?: string; [key: string]: any } | null; // Looser type here if not importing full AppUser
  onManageSubscription?: () => void; // Optional callback for a manage button
}

const SubscriptionStatusDisplay: React.FC<SubscriptionStatusDisplayProps> = ({ currentUser, onManageSubscription }) => {
  if (!currentUser || typeof currentUser.subscriptionStatus === 'undefined') {
    // Don't display anything if no user or subscriptionStatus is not yet loaded/available
    // Or, you could show a loading/default state if preferred
    return null;
  }

  const { subscriptionStatus } = currentUser;

  let statusLabel = 'Unknown';
  let statusColor: "success" | "warning" | "error" | "info" | "default" = 'default';
  let statusIcon = <HelpIcon fontSize="small" />;
  let tooltipText = "Your subscription status is unknown.";

  if (!subscriptionStatus || subscriptionStatus === '' || subscriptionStatus === 'incomplete' || subscriptionStatus === 'incomplete_expired') {
    statusLabel = 'No Active Subscription';
    statusColor = 'info';
    tooltipText = 'You do not have an active subscription.';
  } else if (subscriptionStatus === 'active') {
    statusLabel = 'Active Subscriber';
    statusColor = 'success';
    statusIcon = <CheckCircleIcon fontSize="small" />;
    tooltipText = 'Your subscription is active.';
  } else if (subscriptionStatus === 'trialing') {
    statusLabel = 'On Trial';
    statusColor = 'info';
    statusIcon = <PauseCircleIcon fontSize="small" />;
    tooltipText = 'You are currently on a trial period.';
  } else if (subscriptionStatus === 'past_due' || subscriptionStatus === 'unpaid') {
    statusLabel = 'Payment Issue';
    statusColor = 'warning';
    statusIcon = <ErrorIcon fontSize="small" />;
    tooltipText = 'There is an issue with your subscription payment.';
  } else if (subscriptionStatus === 'canceled') {
    statusLabel = 'Canceled';
    statusColor = 'error';
    statusIcon = <ErrorIcon fontSize="small" />;
    tooltipText = 'Your subscription has been canceled.';
  }
  // Add more specific statuses from Stripe as needed

  return (
    <Tooltip title={tooltipText} arrow>
      <Chip 
        icon={statusIcon}
        label={statusLabel} 
        color={statusColor} 
        size="small"
        variant="outlined"
        sx={{ 
          mr: 2, // Add some margin to the right if placing it left of other items
          cursor: onManageSubscription ? 'pointer' : 'default' 
        }}
        onClick={onManageSubscription} // If a handler is provided, make chip clickable
      />
    </Tooltip>
  );
};

export default SubscriptionStatusDisplay; 